// AF-35 (2026-09-07): AIChatWidget used to collapse EVERY failure --
// network drop, timeout, a real 500, insufficient credits (402), rate
// limiting (429), an auth problem (401/403), even a validation error
// (400) -- into one identical "Sorry, I'm having trouble connecting right
// now" message. The backend already distinguishes these precisely
// (server.js's handleAIGateError for 402/429, core/errors.js's
// sendError() for 400/401/403/404/409, generic 500 otherwise) -- this was
// purely a frontend gap: the response's status/body were never even read.
//
// Deliberately conservative about what's shown verbatim: only a real
// validation message (400) is safe to surface as-is (hand-written,
// user-facing text, same trust level BOB-FINAL-09 established for
// ValidationError on the backend). Everything else gets a fixed, generic-
// but-CATEGORIZED message -- never leaks a raw 500 body/stack/internal
// error text to the user.

export type BobChatErrorKind =
  | 'network'
  | 'cancelled'
  | 'insufficient_credits'
  | 'rate_limited'
  | 'concurrency_limit'
  | 'heavy_operation_limit'
  | 'unauthorized'
  | 'validation'
  | 'server_error'
  | 'unknown';

export interface BobChatErrorInfo {
  kind: BobChatErrorKind;
  message: string;
}

/**
 * Best-effort extraction of a human message from either backend response
 * shape in play today: DomainError's `{ error: "<message>", code }` and
 * handleAIGateError's `{ error: "<code>", message: "<message>" }`. Only
 * used for the one kind (validation) where showing it verbatim is safe.
 */
function extractMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (typeof b.message === 'string' && b.message.trim()) return b.message;
  if (typeof b.error === 'string' && b.error.trim() && !/^[a-z0-9_]+$/.test(b.error)) {
    // A snake_case-only `error` field is a CODE (handleAIGateError's
    // shape), not a message -- only treat it as displayable text if it
    // looks like actual prose (has spaces/punctuation), matching
    // DomainError's shape instead.
    return b.error;
  }
  return null;
}

/**
 * Classifies an HTTP error response from /api/bob-chat (status + parsed
 * JSON body, if available) into a specific kind and a safe, user-facing
 * message.
 */
export function classifyBobChatHttpError(status: number, body: unknown): BobChatErrorInfo {
  switch (status) {
    case 402:
      return { kind: 'insufficient_credits', message: "You're out of AI credits for now. Please check your plan or try again later." };
    case 429: {
      // Bob Plan Limits Phase 3 (2026-09-16): 429 is now shared by THREE
      // distinct backend gates (generic AI rate limiting, the
      // max_concurrent_user=1 "you already have one Bob request running"
      // gate, and the heavy-operation-per-hour cap) -- the body's `error`
      // code (set by handleAIGateError/aiRateLimit in server.js) is what
      // actually distinguishes them; status alone is not enough anymore.
      const code = body && typeof body === 'object' ? (body as Record<string, unknown>).error : undefined;
      if (code === 'concurrency_limit_exceeded') {
        return { kind: 'concurrency_limit', message: "Bob is still working on your last request. Please wait for it to finish before sending another." };
      }
      if (code === 'heavy_operation_rate_limit_exceeded') {
        return { kind: 'heavy_operation_limit', message: "You've reached the hourly limit for large operations like Full Project Setup. Please wait and try again." };
      }
      return { kind: 'rate_limited', message: "You're sending messages a bit too fast. Please wait a moment and try again." };
    }
    case 401:
    case 403:
      return { kind: 'unauthorized', message: "Your session doesn't have access to do that. Try refreshing the page or signing in again." };
    case 400: {
      const detail = extractMessage(body);
      return { kind: 'validation', message: detail || "That message couldn't be processed. Please rephrase and try again." };
    }
    default:
      if (status >= 500) {
        return { kind: 'server_error', message: "Bob is having trouble on our end right now. Please try again in a moment." };
      }
      return { kind: 'unknown', message: "Sorry, I'm having trouble connecting right now. Please try again in a moment." };
  }
}

/** For a network-level failure (fetch itself threw -- no response at all). */
export function classifyBobChatNetworkError(error: unknown): BobChatErrorInfo {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return { kind: 'cancelled', message: '' };
  }
  return { kind: 'network', message: "Can't reach the server right now. Check your connection and try again." };
}
